/**
 * Chat button visibility and the chat + call pill
 * ---------------------------------------------------------------------------------------------------------------------
 * Keeps the Shopify Inbox chat out of the way of the theme's own UI and, when enabled, replaces Shopify's round button
 * with the pill from snippets/chat-call-pill.liquid. Settings: Theme settings → Chat button.
 *
 * THE WIDGET
 * Shopify Inbox renders <shopify-chat> on <body>: a fixed, full-viewport layer with pointer-events: none. Its shadow
 * root holds the round button (part="activator"); once the chat has been used, a nested <shopify-agent-runtime> with
 * its own shadow root holds the button and the chat window (part="dialog", with a "Chat schließen" button in its
 * header). While the window is open the round button is not rendered at all. Everything here therefore searches
 * through nested shadow roots, and treats "a visible part=dialog exists" as "the chat is open".
 *
 * wp-custom.css lowers the layer's z-index below the theme's dialogs, so it always sits behind them.
 *
 * STATES, published on <html> and styled in wp-custom.css
 *   data-chat-state="hidden"   a theme drawer/modal/popover is open (if enabled), or the sticky add-to-cart bar is
 *                              showing and the setting for this screen size is "hide". Never while the chat is open.
 *   data-chat-state="lifted"   the sticky bar is showing, the setting is "move_up", and the button (or the open chat
 *                              window) would overlap it; --chat-lift holds the distance
 *   data-chat-open             the chat window is open
 *   data-chat-bar="hidden"     the chat window is open over the sticky bar and cannot be lifted clear of it without
 *                              running off the top of the screen (full-height mobile window), so the bar steps aside
 *   data-chat-pill-active      the pill is in use: Shopify's own layer is only shown while the window is open
 *   data-chat-beside           mobile, pill in use, setting "beside": the sticky bar always keeps room on its right
 *                              for the pill (--chat-pill-width), so its button and the pill share one row. Set
 *                              whether or not the bar is showing, so the button never jumps in width as it slides in.
 *                              While the bar shows, the pill stays and is only nudged level with the bar's button.
 *
 * Both hiding and lifting act on the whole <shopify-chat> layer, never on the button alone: the button sits in a
 * wrapper that catches clicks, and only the layer takes that invisible hit area with it.
 *
 * WHAT IT LISTENS TO
 *   - Impact's dialogs (x-drawer, cart-drawer, x-modal, x-popover, …) dispatch a bubbling "toggle" event whenever
 *     their open attribute changes. Native <details> and [popover] toggle events do not bubble, so they never get here.
 *   - <product-quick-add> toggles the class "is-visible" when the sticky bar slides in or out.
 *   - Mutations in every shadow root of the chat widget (open/close, button re-renders, unread indicator).
 */
(() => {
  const config = document.currentScript?.dataset || {};
  const hideForDialogs = config.hideForDialogs !== 'false';
  const quickAddMode = { mobile: config.quickAddMobile || 'beside', desktop: config.quickAddDesktop || 'move_up' };

  const root = document.documentElement;
  const desktop = window.matchMedia('(min-width: 700px)');
  const openDialogs = new Set();
  const observedRoots = new WeakSet();
  const GAP = 12;
  const TOP_MARGIN = 16;

  const pill = document.querySelector('[data-chat-pill]');
  let chat = null;
  let quickAdd = null;
  let openRequested = false;

  if (pill) root.dataset.chatPillActive = '';

  /* Shadow DOM helpers ----------------------------------------------------------------------------------------------- */

  const shadowRoots = (start) => {
    const roots = [];
    const walk = (node) => {
      roots.push(node);
      node.querySelectorAll('*').forEach((element) => element.shadowRoot && walk(element.shadowRoot));
    };
    if (start) walk(start);
    return roots;
  };

  const deepQueryAll = (selector) => (chat ? shadowRoots(chat.shadowRoot).flatMap((r) => [...r.querySelectorAll(selector)]) : []);

  const isRendered = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const chatPanel = () => deepQueryAll('[part="dialog"]').find(isRendered) || null;

  // Prefer the button that is actually laid out; the one in the outer shadow root goes stale once the runtime loads.
  const activator = () => {
    const buttons = deepQueryAll('[part="activator"]');
    return buttons.find(isRendered) || buttons[buttons.length - 1] || null;
  };

  const hasUnread = () =>
    deepQueryAll('.activator__indicator').some((element) => getComputedStyle(element).display !== 'none');

  /* Geometry --------------------------------------------------------------------------------------------------------- */

  // Resting box (no lift applied) of what currently sits bottom-right: the open window, the pill, or Shopify's button.
  // The Shopify layer is a full-viewport fixed element whose only offset is our own translate, so subtracting its top
  // undoes the lift. The pill is fixed too; offsetTop/offsetLeft of a fixed element are viewport-relative and ignore
  // transforms.
  const restingBox = (panel) => {
    if (panel) {
      const rect = panel.getBoundingClientRect();
      const hostTop = chat.getBoundingClientRect().top;
      return { top: rect.top - hostTop, bottom: rect.bottom - hostTop, left: rect.left, right: rect.right };
    }

    if (pill) {
      return { top: pill.offsetTop, bottom: pill.offsetTop + pill.offsetHeight, left: pill.offsetLeft, right: pill.offsetLeft + pill.offsetWidth };
    }

    const button = activator();
    if (!button || !chat) return null;
    const rect = button.getBoundingClientRect();
    const hostTop = chat.getBoundingClientRect().top;
    return { top: rect.top - hostTop, bottom: rect.bottom - hostTop, left: rect.left, right: rect.right };
  };

  // How far the box has to move up to clear the sticky bar, or 0 when it does not overlap it. The bar is measured by
  // layout because it is still sliding in when its class changes.
  const liftNeeded = (box) => {
    if (!box || !quickAdd) return 0;

    const barTop = quickAdd.offsetTop;
    const barLeft = quickAdd.offsetLeft;
    const barRight = barLeft + quickAdd.offsetWidth;
    const overlapsHorizontally = box.right > barLeft && box.left < barRight;

    return overlapsHorizontally ? Math.max(0, Math.ceil(box.bottom - barTop + GAP)) : 0;
  };

  // "beside": how far the pill has to move to sit level with the bar's add-to-cart button (bottoms aligned). The CSS
  // already gives both the same bottom margin, so this is normally 0; it only corrects safe-area differences.
  const besideOffset = () => {
    const button = quickAdd?.querySelector('buy-buttons .button, buy-buttons button');
    if (!pill || !button) return 0;

    const buttonBottom = quickAdd.offsetTop + (button.getBoundingClientRect().bottom - quickAdd.getBoundingClientRect().top);
    return Math.round(pill.offsetTop + pill.offsetHeight - buttonBottom);
  };

  const besideActive = () => Boolean(pill && quickAdd && !desktop.matches && quickAddMode.mobile === 'beside');

  /* State ------------------------------------------------------------------------------------------------------------ */

  const update = () => {
    const panel = chatPanel();
    const isOpen = Boolean(panel);
    const barVisible = quickAdd?.classList.contains('is-visible');
    const barMode = desktop.matches ? quickAddMode.desktop : quickAddMode.mobile;

    let state = '';
    let barHidden = false;

    if (isOpen) {
      // The open window never hides. It moves clear of the bar if it fits on screen, otherwise the bar steps aside.
      if (barVisible && barMode !== 'none') {
        const box = restingBox(panel);
        const needed = liftNeeded(box);

        if (needed > 0 && box.top - needed >= TOP_MARGIN) {
          root.style.setProperty('--chat-lift', `${needed}px`);
          state = 'lifted';
        } else if (needed > 0) {
          barHidden = true;
        }
      }
    } else if (hideForDialogs && openDialogs.size > 0) {
      state = 'hidden';
    } else if (barVisible && barMode === 'beside' && besideActive()) {
      const offset = besideOffset();
      if (Math.abs(offset) >= 1) {
        root.style.setProperty('--chat-lift', `${offset}px`);
        state = 'lifted';
      }
    } else if (barVisible && (barMode === 'hide' || barMode === 'beside')) {
      // "beside" without the pill (Shopify's own round button) falls back to hiding.
      state = 'hidden';
    } else if (barVisible && barMode === 'move_up') {
      const needed = liftNeeded(restingBox(null));
      if (needed > 0) {
        root.style.setProperty('--chat-lift', `${needed}px`);
        state = 'lifted';
      }
    }

    setFlag('chatState', state);
    setFlag('chatOpen', isOpen ? '' : null);
    watchWhileOpen(isOpen);
    setFlag('chatBar', barHidden ? 'hidden' : null);

    const beside = besideActive();
    setFlag('chatBeside', beside ? '' : null);
    if (beside) root.style.setProperty('--chat-pill-width', `${pill.offsetWidth}px`);

    if (pill) pill.classList.toggle('chat-pill--unread', hasUnread());

    observeShadowRoots();
  };

  const setFlag = (name, value) => {
    if (value === null || value === undefined || (name === 'chatState' && value === '')) {
      delete root.dataset[name];
    } else if (root.dataset[name] !== value) {
      root.dataset[name] = value;
    }
  };

  // Closing the window ("Chat schließen") changes no attribute and no node that a MutationObserver can see, so while
  // the window is open its presence is re-checked a few times a second. Stops as soon as it is closed.
  let openPoll = null;
  const watchWhileOpen = (isOpen) => {
    if (isOpen && !openPoll) {
      openPoll = setInterval(scheduleUpdate, 300);
    } else if (!isOpen && openPoll) {
      clearInterval(openPoll);
      openPoll = null;
    }
  };

  // Batch bursts of mutations (the chat re-renders a lot when it opens) into one update per frame.
  let frame = null;
  const scheduleUpdate = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      update();
    });
  };

  /* Wiring ----------------------------------------------------------------------------------------------------------- */

  // Every shadow root of the widget, including ones created later (the runtime appears on first open).
  const observeShadowRoots = () => {
    if (!chat?.shadowRoot) return;

    shadowRoots(chat.shadowRoot).forEach((shadow) => {
      if (observedRoots.has(shadow)) return;
      observedRoots.add(shadow);
      // All attributes: the chat closes its window by flipping state attributes whose names are internal to Shopify.
      // Bursts are batched into one update per frame by scheduleUpdate.
      new MutationObserver(scheduleUpdate).observe(shadow, { subtree: true, childList: true, attributes: true });
    });
  };

  // Once the runtime is loaded it treats every real click outside its window as "close": a capture listener records
  // the click and a setTimeout(0) dismisses the chat if nothing else did. Clicking Shopify's button synchronously from
  // the pill's click would open the chat only for that dismissal to close it again, so the button is clicked in a
  // later timer, after Shopify's has run on a still-closed chat (timers with equal delays fire in order).
  const openChat = () => {
    const button = activator();
    if (button) {
      setTimeout(() => button.click());
    } else {
      openRequested = true; // clicked before Shopify's script was ready; opened as soon as it is
    }
  };

  // Theme dialogs
  document.addEventListener('toggle', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLElement) || dialog === pill) return;

    dialog.hasAttribute('open') ? openDialogs.add(dialog) : openDialogs.delete(dialog);
    scheduleUpdate();
  });

  // Sticky add-to-cart bar (product pages only)
  quickAdd = document.querySelector('product-quick-add');
  if (quickAdd) new MutationObserver(scheduleUpdate).observe(quickAdd, { attributes: true, attributeFilter: ['class'] });

  // The pill's chat half
  pill?.querySelector('[data-chat-pill-open]')?.addEventListener('click', openChat);

  // The chat widget is injected by Shopify's own script, usually after this one has run, and only gets its shadow
  // root once chat.js has upgraded it.
  const attachChat = (element) => {
    chat = element;
    window.customElements.whenDefined('shopify-chat').then(() => {
      observeShadowRoots();
      update();

      if (openRequested) {
        openRequested = false;
        // The button inside is rendered a moment after the upgrade.
        const tryOpen = (attempt = 0) => (activator() ? openChat() : attempt < 40 && setTimeout(() => tryOpen(attempt + 1), 100));
        tryOpen();
      }
    });
  };

  const findChat = () => {
    const element = document.querySelector('shopify-chat');
    if (element) attachChat(element);
    return Boolean(element);
  };

  if (!findChat()) {
    const observer = new MutationObserver(() => findChat() && observer.disconnect());
    observer.observe(document.body, { childList: true });

    // No chat on this storefront (app embed off or blocked): the pill keeps only its call half.
    window.addEventListener('load', () => {
      setTimeout(() => {
        if (!chat && pill) pill.classList.add('chat-pill--call-only');
      }, 5000);
    });
  }

  desktop.addEventListener('change', scheduleUpdate);
  window.addEventListener('resize', scheduleUpdate);
  update();
})();
